/**
 * 知识文档详情：版本历史 + 正文 + 切片（含向量状态）。
 *
 * 切片是检索单元，向量状态来自统一的 MemoryIndex。
 */

import { Alert, Button, Card, Checkbox, Descriptions, Form, Input, InputNumber, Modal, Select, Space, Table, Tabs, Typography, message } from "antd";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { api } from "../../services/api";
import { DataTable } from "../../components/DataTable";
import { fmtDateTime, fmtNumber, StatusTag, Tags } from "../../components/format";

interface DocDetail {
  id: string;
  title: string;
  sourceType: string;
  sourceSongId: string | null;
  isCharacterSetting: boolean;
  status: string;
  currentVersionId: string | null;
  knowledgeBase: { id: string; key: string; displayName: string; scope: string; trustLevel: string };
  externalSource: { provider: string; url: string; fetchedAt: string | null; trustLevel: string } | null;
  currentContent: string | null;
  matchKeywords: string[];
  chunkingParams: unknown;
  versions: {
    id: string;
    version: number;
    title: string;
    content: string;
    summary: string | null;
    matchKeywords: string[];
    isCharacterSetting: boolean;
    chunkingParams: { targetChars?: number; overlapChars?: number; splitBy?: string };
    contentHash: string;
    status: string;
    chunkCount: number;
    publishedAt: string | null;
    publishedBy: string | null;
    buildStartedAt: string | null;
    buildCompletedAt: string | null;
    buildError: string | null;
    createdAt: string | null;
  }[];
}

export function KnowledgeDocumentPage() {
  const { id = "" } = useParams();
  const [versionId, setVersionId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [publishVersion, setPublishVersion] = useState<DocDetail["versions"][number] | null>(null);
  const [saving, setSaving] = useState(false);
  const [editForm] = Form.useForm();
  const [publishForm] = Form.useForm();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["knowledgeDoc", id],
    queryFn: () => api.get<DocDetail | null>(`/api/v1/admin/knowledge/documents/${id}`),
  });

  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;
  const activeVersion = versionId ?? data?.currentVersionId ?? null;
  const selected = data?.versions.find((version) => version.id === activeVersion) ?? data?.versions[0] ?? null;

  const openEdit = () => {
    const base = data?.versions[0];
    if (!base) return;
    editForm.setFieldsValue({
      title: base.title,
      content: base.content,
      summary: base.summary ?? "",
      matchKeywords: base.matchKeywords,
      isCharacterSetting: base.isCharacterSetting,
      targetChars: base.chunkingParams?.targetChars ?? 500,
      overlapChars: base.chunkingParams?.overlapChars ?? 80,
      splitBy: base.chunkingParams?.splitBy ?? "auto",
      reason: "",
    });
    setEditOpen(true);
  };

  const saveDraft = async () => {
    const values = await editForm.validateFields();
    setSaving(true);
    try {
      const draft = await api.patch<{ id: string }>(`/api/v1/admin/knowledge/documents/${id}/draft`, {
        title: values.title,
        content: values.content,
        summary: values.summary?.trim() || null,
        matchKeywords: values.matchKeywords ?? [],
        isCharacterSetting: values.isCharacterSetting,
        chunkingParams: {
          targetChars: values.targetChars,
          overlapChars: values.overlapChars,
          splitBy: values.splitBy,
        },
        reason: values.reason,
      });
      message.success("草稿已保存，当前线上版本未改变");
      setVersionId(draft.id);
      setEditOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["knowledgeDoc", id] });
    } finally {
      setSaving(false);
    }
  };

  const doPublish = async () => {
    if (!publishVersion) return;
    const values = await publishForm.validateFields();
    setSaving(true);
    try {
      await api.post(`/api/v1/admin/knowledge/versions/${publishVersion.id}/publish`, values);
      message.success(`v${publishVersion.version} 已进入后台构建，旧版本会继续服务`);
      setPublishVersion(null);
      await queryClient.invalidateQueries({ queryKey: ["knowledgeDoc", id] });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Card
        title={data?.title ?? "文档"}
        extra={data?.sourceType === "song" ? <Typography.Text type="secondary">请在曲库修改歌词</Typography.Text> : <Button onClick={openEdit}>编辑为新版本</Button>}
        loading={isLoading}
        styles={{ body: { padding: 16 } }}
      >
        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="知识库">
            {data?.knowledgeBase.displayName}（{data?.knowledgeBase.key}）
          </Descriptions.Item>
          <Descriptions.Item label="范围">{data?.knowledgeBase.scope}</Descriptions.Item>
          <Descriptions.Item label="可信级别">
            <StatusTag value={data?.knowledgeBase.trustLevel} />
          </Descriptions.Item>
          <Descriptions.Item label="来源类型">{data?.sourceType}</Descriptions.Item>
          <Descriptions.Item label="角色设定">{data?.isCharacterSetting ? "是" : "否"}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <StatusTag value={data?.status} />
          </Descriptions.Item>
          <Descriptions.Item label="匹配关键词" span={3}>
            <Tags values={data?.matchKeywords ?? []} />
          </Descriptions.Item>
          <Descriptions.Item label="外部来源">
            {data?.externalSource ? `${data.externalSource.provider} · ${data.externalSource.url}` : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="切分参数" span={3}>
            <span className="mono">{JSON.stringify(data?.chunkingParams ?? null)}</span>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs
        items={[
          {
            key: "versions",
            label: `版本 ${data?.versions.length ?? 0}`,
            children: (
              <Card styles={{ body: { padding: 12 } }}>
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  loading={isLoading}
                  dataSource={data?.versions ?? []}
                  rowClassName={(r) => (r.id === activeVersion ? "ant-table-row-selected" : "")}
                  onRow={(r) => ({ onClick: () => setVersionId(r.id), style: { cursor: "pointer" } })}
                  columns={[
                    { title: "版本", dataIndex: "version", width: 80, render: (v: number) => `v${v}` },
                    { title: "状态", dataIndex: "status", width: 110, render: (v) => <StatusTag value={v} /> },
                    { title: "摘要", dataIndex: "summary", render: (v) => v || "—" },
                    { title: "匹配关键词", dataIndex: "matchKeywords", width: 180, render: (v: string[]) => <Tags values={v} /> },
                    { title: "切片", dataIndex: "chunkCount", width: 80, align: "right" },
                    { title: "内容哈希", dataIndex: "contentHash", width: 110, render: (v: string) => v?.slice(0, 10) },
                    { title: "发布者", dataIndex: "publishedBy", width: 110, render: (v) => v || "—" },
                    { title: "发布时间", dataIndex: "publishedAt", width: 170, render: fmtDateTime },
                    {
                      title: "操作",
                      width: 100,
                      render: (_, version) =>
                        version.status === "draft" || version.status === "failed" ? (
                          <Button size="small" type="link" onClick={(event) => { event.stopPropagation(); setPublishVersion(version); }}>
                            {version.status === "failed" ? "重试发布" : "发布"}
                          </Button>
                        ) : null,
                    },
                  ]}
                  expandable={{
                    expandedRowRender: (version) => version.buildError ? <Alert type="error" showIcon message="构建失败" description={version.buildError} /> : <Typography.Text type="secondary">构建开始：{fmtDateTime(version.buildStartedAt)}　完成：{fmtDateTime(version.buildCompletedAt)}</Typography.Text>,
                    rowExpandable: (version) => Boolean(version.buildError || version.buildStartedAt),
                  }}
                />
              </Card>
            ),
          },
          {
            key: "chunks",
            label: "切片与向量",
            children: activeVersion ? (
              <DataTable
                path={`/api/v1/admin/knowledge/versions/${activeVersion}/chunks`}
                defaultPageSize={20}
                columns={[
                  { title: "#", dataIndex: "index", width: 60, align: "right" },
                  {
                    title: "标题路径",
                    dataIndex: "headingPath",
                    width: 200,
                    render: (v: string[]) => (v?.length ? v.join(" / ") : "—"),
                  },
                  { title: "内容", dataIndex: "content" },
                  { title: "标签", dataIndex: "tags", width: 160, render: (v: string[]) => <Tags values={v} /> },
                  { title: "token", dataIndex: "tokenCount", width: 80, align: "right", render: fmtNumber },
                  { title: "可信级别", dataIndex: "trustLevel", width: 120, render: (v) => <StatusTag value={v} /> },
                  {
                    title: "启用",
                    dataIndex: "isActive",
                    width: 70,
                    render: (v: boolean) => (v ? "是" : "否"),
                  },
                  {
                    title: "向量",
                    dataIndex: "embedding",
                    width: 150,
                    render: (v: { model: string; dimensions: number; status: string } | null) =>
                      v ? (
                        <>
                          <StatusTag value={v.status} /> {v.dimensions}d
                        </>
                      ) : (
                        <Typography.Text type="secondary">未向量化</Typography.Text>
                      ),
                  },
                ]}
              />
            ) : (
              <Alert type="info" message="请先在「版本」页签选择一个版本" />
            ),
          },
          {
            key: "content",
            label: "版本正文",
            children: (
              <Card styles={{ body: { padding: 16 } }}>
                <div className="mono" style={{ whiteSpace: "pre-wrap" }}>{selected?.content ?? "（无正文）"}</div>
              </Card>
            ),
          },
        ]}
      />

      <Modal title="编辑知识草稿" open={editOpen} onCancel={() => setEditOpen(false)} onOk={() => void saveDraft()} confirmLoading={saving} width={820} destroyOnClose>
        <Alert type="info" showIcon message="保存只创建或更新草稿；发布成功前，当前线上版本与召回结果不会改变。" />
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="标题" rules={[{ required: true }]}><Input maxLength={200} /></Form.Item>
          <Form.Item name="content" label="正文" rules={[{ required: true }]}><Input.TextArea rows={16} maxLength={500_000} showCount /></Form.Item>
          <Form.Item name="summary" label="摘要"><Input.TextArea rows={2} maxLength={2_000} /></Form.Item>
          <Form.Item
            name="matchKeywords"
            label="匹配关键词"
            extra="填写别名、昵称或专名；发布后，用户消息包含其中任一词时会优先召回本篇。每个词至少 2 个字。"
          >
            <Select mode="tags" open={false} tokenSeparators={[",", "，", "、"]} maxTagCount="responsive" placeholder="输入后按回车，例如：司总、大王" />
          </Form.Item>
          <Form.Item name="isCharacterSetting" valuePropName="checked"><Checkbox>这是角色自身设定</Checkbox></Form.Item>
          <Space align="start">
            <Form.Item name="targetChars" label="目标切片字数"><InputNumber min={50} max={4_000} /></Form.Item>
            <Form.Item name="overlapChars" label="重叠字数"><InputNumber min={0} max={500} /></Form.Item>
            <Form.Item name="splitBy" label="切片策略"><Select style={{ width: 150 }} options={["auto", "paragraph", "song-line"].map((value) => ({ value, label: value }))} /></Form.Item>
          </Space>
          <Form.Item name="reason" label="操作理由" rules={[{ required: true }]}><Input maxLength={200} /></Form.Item>
        </Form>
      </Modal>

      <Modal title={`发布 v${publishVersion?.version ?? ""}`} open={Boolean(publishVersion)} onCancel={() => setPublishVersion(null)} onOk={() => void doPublish()} confirmLoading={saving} destroyOnClose>
        <Alert type="info" showIcon message="后台会先生成全部切片与向量并校验数量，再原子替换当前版本。构建失败时旧版继续召回。" />
        <Form form={publishForm} layout="vertical" preserve={false} style={{ marginTop: 16 }}>
          <Form.Item name="reason" label="发布理由" rules={[{ required: true }]}><Input maxLength={200} /></Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
